import PropTypes from 'prop-types';
import React from 'react';
import '../styles/totalsTable.css';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const Row = ({ label, value, bold }) => (
  <div className={`od-order-summary-row${bold ? ' is-bold' : ''}`}>
    <span className="od-order-summary-label">{label}</span>
    <span className="od-order-summary-value">{value}</span>
  </div>
);

const Divider = () => <div className="od-order-summary-divider" />;

const parseSeatAssignment = (assignment) => {
  const parts = String(assignment || '').split(',').map((s) => s.trim()).filter(Boolean);
  let section = null;
  let row = null;
  let rowHeader = 'Row';
  let seat = null;
  const hasSeatPart = parts.some((part) => /^seat\s+/i.test(part));
  parts.forEach((part) => {
    if (/^section\s+/i.test(part)) { section = part.replace(/^section\s+/i, ''); }
    else if (/^row\s+/i.test(part)) { row = part.replace(/^row\s+/i, ''); }
    else if (hasSeatPart && /^table\s+/i.test(part)) { row = part.replace(/^table\s+/i, ''); rowHeader = 'Table'; }
    else if (/^seat\s+/i.test(part)) { seat = part.replace(/^seat\s+/i, ''); }
    else if (seat == null) { seat = part; }
  });
  return { section, row, rowHeader, seat: seat == null ? String(assignment || '') : seat };
};

const sortSeats = (a, b) => {
  const na = parseInt(a, 10);
  const nb = parseInt(b, 10);
  if (Number.isNaN(na) || Number.isNaN(nb)) { return String(a).localeCompare(String(b)); }
  return na - nb;
};

const groupSeatedObjects = (objects) => {
  const order = [];
  const byKey = new Map();
  objects.forEach((object) => {
    const { section, row, rowHeader, seat } = parseSeatAssignment(object.seat_assignment);
    const sectionKey = section || '';
    let sectionGroup = byKey.get(sectionKey);
    if (!sectionGroup) {
      sectionGroup = { section, count: 0, rowOrder: [], rowByKey: new Map() };
      byKey.set(sectionKey, sectionGroup);
      order.push(sectionGroup);
    }
    sectionGroup.count += 1;
    const rowKey = `${rowHeader}|${row || ''}`;
    let rowGroup = sectionGroup.rowByKey.get(rowKey);
    if (!rowGroup) {
      rowGroup = { row, rowHeader, seats: [] };
      sectionGroup.rowByKey.set(rowKey, rowGroup);
      sectionGroup.rowOrder.push(rowGroup);
    }
    rowGroup.seats.push(seat);
  });
  return order;
};

const dedupeByObjectId = (objects) => {
  const seen = new Set();
  return objects.filter((o) => {
    const id = o.seatsio_object_id;
    if (id == null) { return true; }
    if (seen.has(id)) { return false; }
    seen.add(id);
    return true;
  });
};

const SeatedGroups = ({ product }) => {
  const rawObjects = product.seatsIOObjects || [];
  const seatType = rawObjects[0]?.seat_type;
  const objects = seatType === 'Table' ? dedupeByObjectId(rawObjects) : rawObjects;
  const priceValue = (count) => (product.free ? 'FREE' : `${count} × ${product.price_to_currency}`);

  if (seatType === 'AddOn') {
    return <Row label={product.name} value={priceValue(objects.length)} />;
  }

  return (
    <>
      {groupSeatedObjects(objects).map((sectionGroup, sectionIndex) => (
        <React.Fragment key={sectionIndex}>
          <Row
            label={`${product.name}${sectionGroup.section ? ` · ${sectionGroup.section}` : ''}`}
            value={priceValue(sectionGroup.count)}
          />
          {sectionGroup.rowOrder.map((rowGroup, rowIndex) => {
            const seats = [...rowGroup.seats].sort(sortSeats);
            const subline = seatType === 'Table'
              ? seats.join(', ')
              : `${rowGroup.row ? `${rowGroup.rowHeader} ${rowGroup.row} · ` : ''}${seats.length > 1 ? 'Seats' : 'Seat'} ${seats.join(', ')}`;
            return (
              <div className="od-order-summary-subline" key={rowIndex}>
                {subline}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
};

const TotalsTable = ({
  ticketTypes,
  promoCode = {},
  ticketReservation = {},
  addOns = [],
  confirm,
  ticketInsurance,
  selectedMembershipProduct,
  resaleOrderItems
}) => {
  const membershipPrice = selectedMembershipProduct && selectedMembershipProduct.ticketing_price
    ? selectedMembershipProduct.ticketing_price
    : null;
  const membershipSubtotal = membershipPrice ? (membershipPrice.amount_cents || 0) / 100 : 0;
  const membershipFees = membershipPrice && !selectedMembershipProduct.absorb_fees
    ? (membershipPrice.total_fee_amount_cents || 0) / 100
    : 0;
  const membershipTax = membershipPrice
    ? (membershipPrice.total_tax_amount_cents || 0) / 100
    : 0;
  const membershipTotal = membershipPrice ? (membershipPrice.customer_pays_cents || 0) / 100 : 0;
  const ticketFees = ticketReservation.in_memory_fees || [];

  const resaleLineItems = (resaleOrderItems || [])
    .map((s) => ({
      key: s.listingId,
      ticketTypeName: s.ticketTypeName,
      unitPrice: (s.listPriceCents || 0) / 100,
      quantity: s.quantity
    }));

  const hasContent = Object.keys(ticketReservation).length > 0 || selectedMembershipProduct || resaleLineItems.length > 0;
  if (!hasContent) return null;

  const showPromoLine = Object.keys(promoCode).length > 0
    && typeof promoCode.discount_label !== 'undefined'
    && typeof ticketReservation.discount !== 'undefined'
    && ticketReservation.discount > 0.00;

  const showTaxes = (parseFloat(ticketReservation.taxes || 0) + membershipTax) > 0.00;

  return (
    <div className="od-order-summary">
      <div className="od-order-summary-title">Order Summary</div>

      {ticketTypes.filter((ti) => ti.quantity && ti.quantity > 0).map((ticketType, index) =>
        ticketType.seatsIOObjects && ticketType.seatsIOObjects.length > 0 ? (
          <SeatedGroups key={index} product={ticketType} />
        ) : (
          <Row
            key={index}
            label={`${ticketType.quantity} x ${ticketType.name}`}
            value={ticketType.free ? 'FREE' : formatter.format(ticketType.quantity * parseFloat(ticketType.price || 0))}
          />
        )
      )}

      {addOns.filter((ao) => ao.quantity && ao.quantity > 0).map((addOn, index) =>
        addOn.seatsIOObjects && addOn.seatsIOObjects.length > 0 ? (
          <SeatedGroups key={`addon-${index}`} product={addOn} />
        ) : (
          <Row
            key={`addon-${index}`}
            label={`${addOn.quantity} x ${addOn.name}`}
            value={formatter.format(addOn.quantity * parseFloat(addOn.price || 0))}
          />
        )
      )}

      {membershipPrice ? (
        <Row label={`1 x ${selectedMembershipProduct.name}`} value={membershipPrice.price_to_currency} />
      ) : null}

      {resaleLineItems.length > 0 ? (
        <>
          <div className="od-order-summary-section-label">Verified Resell</div>
          {resaleLineItems.map((item) =>
            <Row key={item.key} label={`${item.quantity} x ${item.ticketTypeName}`} value={formatter.format(item.quantity * item.unitPrice)} />
          )}
        </>
      ) : null}

      {showPromoLine ? (
        <>
          <Divider />
          <Row label={promoCode.discount_label} value={`-${formatter.format(ticketReservation.discount)}`} />
        </>
      ) : null}

      <Divider />

      <Row
        label="Subtotal"
        value={formatter.format(parseFloat(ticketReservation.subtotal_with_discount || 0.00) + membershipSubtotal)}
      />

      {(ticketReservation.abstract_order_items || []).map((abstractOrderItem, index) =>
        <Row
          key={`abstract-${index}`}
          label={abstractOrderItem.custom_charge_type_type === 'CustomChargeTypes::Donation'
            ? abstractOrderItem.name
            : `${abstractOrderItem.quantity} x ${abstractOrderItem.price_to_currency} ${abstractOrderItem.name}`}
          value={abstractOrderItem.summed_price_to_currency}
        />
      )}

      {ticketFees.map((fee, index) =>
        <Row key={`fee-${index}`} label={fee.name} value={formatter.format(fee.value)} />
      )}

      {membershipFees > 0 ? (
        <Row label="Membership Fees" value={formatter.format(membershipFees)} />
      ) : null}

      {showTaxes ? (
        <Row label="Taxes" value={formatter.format(parseFloat(ticketReservation.taxes || 0) + membershipTax)} />
      ) : null}

      {ticketReservation.gift_up_redemption ? (
        <>
          <Divider />
          <Row
            label={`${ticketReservation.gift_up_redemption.gift_up_giftcard.title} (${ticketReservation.gift_up_redemption.gift_up_giftcard.code})`}
            value={`-${formatter.format(ticketReservation.gift_up_redemption.redeemed_amount)}`}
          />
        </>
      ) : null}

      {(ticketReservation.applied_gift_card_summaries || []).length > 0 ? (
        <>
          <Divider />
          {ticketReservation.applied_gift_card_summaries.map((giftCard) =>
            <Row
              key={`gift-card-${giftCard.id}`}
              label={`Gift card •••• ${giftCard.code_last4}`}
              value={`-${formatter.format(giftCard.amount_cents / 100)}`}
            />
          )}
        </>
      ) : null}

      <Divider />

      <Row
        label="Total"
        bold
        value={formatter.format(parseFloat(ticketReservation.gift_card_adjusted_total || 0.00) + membershipTotal)}
      />

      {ticketInsurance && ticketInsurance.quote_id ? (
        <Row label="Insurance" bold value={formatter.format(ticketInsurance.premium_amount)} />
      ) : null}

      {selectedMembershipProduct ? (
        <div className="od-order-summary-note">
          Your membership will be processed as a separate charge and will appear separately on your bank statement.
        </div>
      ) : null}

      {ticketReservation.donation_separated ? (
        <div className="od-order-summary-note">
          Your donation will be processed as a separate charge and will appear separately on your bank statement.
        </div>
      ) : null}

      {confirm.checkout_widget_memo && confirm.checkout_widget_memo.length > 0 ? (
        <div className="od-order-summary-note">{confirm.checkout_widget_memo}</div>
      ) : null}
    </div>
  );
};

TotalsTable.propTypes = {
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  ticketReservation: PropTypes.object,
  addOns: PropTypes.array,
  seatsIOSelectedObjects: PropTypes.array,
  confirm: PropTypes.object.isRequired,
  ticketInsurance: PropTypes.object,
  selectedMembershipProduct: PropTypes.object,
  resaleOrderItems: PropTypes.array
};

export default TotalsTable;
